import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  RefreshControl,
  Modal,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { formatPct } from '../../utils/formatPercentage';
import Svg, { Path, Circle, G, Rect, Line } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { employeesApi, kpisApi, processesApi } from '../../services/apiService';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';
import { Employee, KPIList, ProcessList } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useRoleNavigation } from '../../hooks';

type RouteProps = RouteProp<MainStackParamList, 'EmployeeDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UserAvatarIcon = ({ size = 48 }: { size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <Circle cx="24" cy="24" r="23" fill="#F3F4F6" stroke="#E5E7EB" strokeWidth="2"/>
    <Path d="M24 26C28.4183 26 32 22.4183 32 18C32 13.5817 28.4183 10 24 10C19.5817 10 16 13.5817 16 18C16 22.4183 19.5817 26 24 26Z" fill="#D1D5DB"/>
    <Path d="M10 40C10 33.3726 15.3726 28 22 28H26C32.6274 28 38 33.3726 38 40" fill="#D1D5DB"/>
  </Svg>
);

// Circular Progress Component
const CircularProgress = ({ percentage, size = 100, showLabel = true }: { percentage: number; size?: number; showLabel?: boolean }) => {
  const strokeWidth = size > 80 ? 8 : 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  let progressColor = '#EF4444'; // Red - Needs Improvement (< 80%)
  if (percentage >= 100) progressColor = '#22C55E'; // Green - Excellent
  else if (percentage >= 80) progressColor = '#F97316'; // Orange - Good

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      {showLabel && size > 80 && (
        <View style={{ position: 'absolute', alignItems: 'center' }}>
          <Text style={{ fontSize: 24, fontWeight: '700', color: '#1F2937' }}>{percentage} %</Text>
        </View>
      )}
    </View>
  );
};

// Progress Bar Component - RTL (fills from right to left)
const ProgressBar = ({ percentage, color = '#22C55E' }: { percentage: number; color?: string }) => (
  <View style={styles.progressBarContainer}>
    <View style={[styles.progressBarFill, { width: `${Math.min(percentage, 100)}%`, backgroundColor: color }]} />
    <View style={[styles.progressDot, { right: `${Math.min(percentage, 100)}%`, backgroundColor: color }]} />
  </View>
);

// ============================================================
// Info Row Component - Table style matching Figma
// ============================================================

interface InfoRowProps {
  label: string;
  value: string | number | undefined | null;
}

const InfoRow = ({ label, value }: InfoRowProps) => (
  <View style={styles.infoRow}>
    <View style={styles.infoLabelBox}>
      <Text style={styles.infoLabel}>{label}</Text>
    </View>
    <Text style={styles.infoValue}>{value || '-'}</Text>
  </View>
);

// Stat Row for إحصائيات الأداء
const StatRow = ({ label, value }: { label: string; value: string | number }) => (
  <View style={styles.statRow}>
    <Text style={styles.statValue}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

// Small Circular Progress for percentage badge (donut style)
const SmallCircularProgress = ({ percentage, size = 32 }: { percentage: number; size?: number }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circle */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke="#E5E7EB"
        strokeWidth={strokeWidth}
        fill="transparent"
      />
      {/* Progress arc */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke="#22C55E"
        strokeWidth={strokeWidth}
        fill="transparent"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
      />
    </Svg>
  );
};

// KPI Card Component - Unified with UnitGoalDetailScreen/ServiceDetailScreen
interface KPICardProps {
  kpi: KPIList;
  onPress: () => void;
}

const KPICard = ({ kpi, onPress, viewDetailsText }: KPICardProps & { viewDetailsText: string }) => {
  const { colors, isDark } = useTheme();
  const achievement = kpi.achievement_percentage || 0;
  const currentValue = kpi.latest_value || '0';
  const targetValue = kpi.target_value || '0';

  // Get color based on achievement percentage
  const getProgressColor = (achievement: number) => {
    if (achievement >= 80) return '#2CA02C';
    if (achievement >= 50) return '#FF7F0E';
    return '#D62728';
  };

  // Format value display - show percentage if it's a percentage-based KPI
  const isPercentage = targetValue === '100' || parseFloat(targetValue) === 100;
  const displayValue = isPercentage
    ? `% ${formatPct(achievement)}`
    : `${targetValue}\\${currentValue}`;

  const progressColor = getProgressColor(achievement);

  return (
    <TouchableOpacity style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]} onPress={onPress} activeOpacity={0.7}>
      {/* KPI Title */}
      <Text style={[styles.kpiCardTitle, { color: colors.textPrimary }]} numberOfLines={2}>
        {kpi.name}
      </Text>

      {/* Progress Bar Row */}
      <View style={styles.kpiCardStats}>
        {/* Value Badge */}
        <View style={[styles.kpiValueBadge, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]}>
          <Text style={[styles.kpiValueText, { color: colors.textPrimary }]}>{displayValue}</Text>
        </View>

        {/* Progress Bar with dot indicator */}
        <View style={styles.kpiCardProgress}>
          <View style={[styles.kpiProgressBarBg, { backgroundColor: isDark ? colors.gray200 : '#E5E7EB' }]}>
            <View
              style={[
                styles.kpiProgressBarFill,
                {
                  width: `${Math.min(achievement, 100)}%`,
                  backgroundColor: progressColor
                }
              ]}
            />
          </View>
          {/* Progress dot with border */}
          <View style={[styles.kpiProgressDotOuter, { borderColor: progressColor }]}>
            <View style={[styles.kpiProgressDotInner, { backgroundColor: progressColor }]} />
          </View>
        </View>
      </View>

      {/* View Details Link */}
      <View style={styles.kpiCardLink}>
        <ChevronLeftIcon size={14} color={colors.textMuted} />
        <Text style={[styles.kpiCardLinkText, { color: colors.textMuted }]}>{viewDetailsText}</Text>
      </View>
    </TouchableOpacity>
  );
};

// Process Card Component
const ProcessCard = ({ name, percentage, onViewDetails, viewDetailsText }: { name: string; percentage: number; onViewDetails?: () => void; viewDetailsText: string }) => {
  const { colors, isDark } = useTheme();
  return (
    <View style={[styles.processCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <Text style={[styles.processCardTitle, { color: colors.textPrimary }]}>{name}</Text>
      <View style={styles.kpiProgressRow}>
        <View style={styles.kpiPercentageContainer}>
          <Text style={[styles.kpiPercentage, { color: colors.textPrimary }]}>{percentage} %</Text>
          <View style={styles.kpiDot} />
        </View>
        <ProgressBar percentage={percentage} />
      </View>
      <TouchableOpacity onPress={onViewDetails} style={styles.kpiDetailsLink}>
        <ChevronLeftIcon size={14} color={colors.textMuted} />
        <Text style={[styles.kpiDetailsText, { color: colors.textMuted }]}>{viewDetailsText}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Helper Functions
// ============================================================

const formatArabicDate = (dateString: string): string => {
  const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const parts = dateString.split('-');
  if (parts.length === 3) {
    const year = parts[0];
    const month = parseInt(parts[1]) - 1;
    const day = parts[2];
    return `${day} ${arabicMonths[month]}، ${year}`;
  }
  return dateString;
};

// ============================================================
// Main Component
// ============================================================

export default function EmployeeDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id, source } = route.params as { id: number; source?: 'myorganization' | 'employees' };
  const isFromEmployees = source === 'employees';
  const { width } = useWindowDimensions();
  const { showNotification } = useNotification();
  const { navigateToHome } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { selectedYear } = useYear();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : '#F1F5F9',
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [processes, setProcesses] = useState<ProcessList[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);

  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1200;

  const fetchEmployee = useCallback(async () => {
    try {
      const data = await employeesApi.get(id);
      setEmployee(data);

      // Fetch KPIs linked to this employee
      setKpisLoading(true);
      try {
        console.log('Fetching KPIs for employee ID:', id);
        const kpisData = await kpisApi.list({ employee: id, page_size: 100 });
        console.log('KPIs fetched:', kpisData.results?.length || 0, kpisData.results);
        setKpis(kpisData.results || []);
      } catch (err) {
        console.error('Failed to fetch KPIs:', err);
      } finally {
        setKpisLoading(false);
      }

      // Fetch processes where employee is assigned to steps (via ManyToMany)
      setProcessesLoading(true);
      try {
        const processesData = await employeesApi.processes(id);
        console.log('Assigned processes fetched:', processesData.total_processes, processesData.processes);
        setProcesses(processesData.processes || []);
      } catch (err) {
        console.error('Failed to fetch assigned processes:', err);
        // Fallback: try to fetch processes by organizational unit if the new endpoint fails
        if (data.organizational_unit) {
          try {
            const fallbackData = await processesApi.list({
              organizational_unit: data.organizational_unit,
              page_size: 100
            });
            setProcesses(fallbackData.results || []);
          } catch (fallbackErr) {
            console.error('Fallback fetch also failed:', fallbackErr);
          }
        }
      } finally {
        setProcessesLoading(false);
      }
    } catch (err: any) {
      console.error('Failed to fetch employee:', err);
      showNotification(t('employees.loadFailed'), 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, showNotification]);

  useFocusEffect(
    useCallback(() => {
      fetchEmployee();
    }, [fetchEmployee])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchEmployee();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchEmployee();
  };

  const handleEdit = () => {
    navigation.navigate('AddEmployee', { employeeId: id });
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await employeesApi.delete(id);
      showNotification(t('employees.deleteSuccess'), 'success');
      invalidateData('my-organization');
      navigation.goBack();
    } catch (err: any) {
      console.error('Failed to delete employee:', err);
      showNotification(t('employees.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
      setDeleteModalVisible(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  if (!employee) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('employees.notFound')}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => navigation.goBack()}>
            <Text style={styles.retryButtonText}>{t('common.back')}</Text>
          </TouchableOpacity>
        </View>
      </PageContainer>
    );
  }

  // Use backend's weighted average calculation for overall achievement
  // Formula: sum(achievement × weight) / sum(weights)
  const overallPercentage = parseFloat(Number(employee.overall_achievement || 0).toFixed(2));

  // Use backend's total readings count
  const totalReadings = employee.readings_count || 0;

  // Get last reading value
  const lastReading = kpis.length > 0 && kpis[0].latest_value
    ? `${kpis[0].latest_value} ${kpis[0].unit || '%'}`
    : '-';

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home') },
            { label: isFromEmployees ? t('navigation.humanCapital') : t('navigation.myOrganization'), route: isFromEmployees ? 'Employees' : 'MyOrganization' },
            { label: t('employees.employeeDetails') },
          ]}
        />

        {/* Header Section */}
        <View style={styles.headerSection}>
          <View style={styles.employeeHeader}>
            <View style={styles.avatarLarge}>
              {employee.profile_image ? (
                <Image
                  source={{ uri: employee.profile_image }}
                  style={styles.avatarImage}
                  resizeMode="cover"
                />
              ) : (
                <UserAvatarIcon size={56} />
              )}
            </View>
            <View style={styles.employeeHeaderInfo}>
              <Text style={styles.employeeNameLarge}>{employee.name_ar}</Text>
              <Text style={styles.employeeJobTitle}>
                {employee.position} - {employee.organizational_unit_name || t('employees.organizationalUnit')}
              </Text>
            </View>
          </View>

          <TouchableOpacity style={styles.editButton} onPress={handleEdit}>
            <EditIcon size={14} color="#414042" />
            <Text style={styles.editButtonText}>{t('employees.editData')}</Text>
          </TouchableOpacity>
        </View>

        {/* Row 1: Three Info Cards */}
        <View style={[styles.cardsRow, isSmallScreen && styles.cardsRowSmall]}>
          {/* Card 1: المعلومات الشخصية */}
          <View style={[styles.card, isSmallScreen && styles.cardFullWidth]}>
            <Text style={styles.cardTitleSimple}>{t('employees.personalInfo')}</Text>
            <View style={styles.cardBodySimple}>
              <InfoRow label={t('employees.name')} value={employee.name_ar} />
              <InfoRow label={t('employees.emailLabel')} value={employee.email || '-'} />
              <InfoRow label={t('employees.phoneLabel')} value={employee.phone || '-'} />
              <InfoRow label={t('employees.birthDate')} value={employee.birth_date ? formatArabicDate(employee.birth_date) : '-'} />
              <InfoRow label={t('employees.genderLabel')} value={employee.gender_display || (employee.gender === 'female' ? t('employees.female') : t('employees.male'))} />
            </View>
          </View>

          {/* Card 2: الملف التعريفي */}
          <View style={[styles.card, isSmallScreen && styles.cardFullWidth]}>
            <Text style={styles.cardTitleSimple}>{t('employees.profileSection')}</Text>
            <View style={styles.cardBodySimple}>
              <InfoRow label={t('employees.organizationLabel')} value={employee.department_name || '-'} />
              <InfoRow label={t('employees.organizationalUnit')} value={employee.organizational_unit_name || '-'} />
              <InfoRow label={t('employees.type')} value={t('employees.employee')} />
              <InfoRow label={t('employees.jobTitle')} value={employee.position || t('employees.adminEmployee')} />
              <InfoRow label={t('employees.registrationDate')} value={employee.created_at ? new Date(employee.created_at).toLocaleDateString('ar-SA') : '-'} />
            </View>
          </View>

          {/* Card 3: إحصائيات الأداء */}
          <View style={[styles.card, isSmallScreen && styles.cardFullWidth]}>
            <Text style={styles.cardTitleSimple}>{t('employees.performanceStats')}</Text>
            <View style={styles.statsCardLayout}>
              {/* Left: Circular Progress */}
              <View style={styles.statsCircleContainer}>
                <CircularProgress percentage={overallPercentage} size={90} showLabel={false} />
                <Text style={styles.statsCirclePercent}>{overallPercentage} %</Text>
                <Text style={styles.statsCircleLabel}>{t('employees.overallPerformance')}</Text>
              </View>
              {/* Right: Stats Table */}
              <View style={styles.statsTableContainer}>
                <InfoRow label={t('employees.kpisCount')} value={kpis.length.toString()} />
                <InfoRow label={t('employees.readingsCount')} value={totalReadings.toString()} />
                <InfoRow label={t('employees.lastReading')} value={lastReading} />
              </View>
            </View>
          </View>
        </View>

        {/* Row 2: KPI Section with Trend Chart */}
        <View style={[styles.kpiTrendRow, isSmallScreen && styles.kpiTrendRowSmall]}>
          {/* Left: اتجاه المؤشر - Trend Chart */}
          <View style={[styles.trendChartColumn, isSmallScreen && styles.trendChartColumnSmall]}>
            <View style={[styles.sideCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.sideCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.kpiTrend')}</Text>
              <View style={styles.chartWrapper}>
                {/* Chart Grid Lines */}
                <View style={styles.chartGridContainer}>
                  <View style={styles.chartGridRow}>
                    <Text style={styles.chartYLabel}>100%</Text>
                    <View style={styles.chartGridLine} />
                  </View>
                  <View style={styles.chartGridRow}>
                    <Text style={styles.chartYLabel}>50%</Text>
                    <View style={styles.chartGridLine} />
                  </View>
                  <View style={styles.chartGridRow}>
                    <Text style={styles.chartYLabel}>0%</Text>
                    <View style={styles.chartGridLine} />
                  </View>
                </View>
                {/* Chart Line */}
                <Svg width="100%" height={100} style={styles.chartSvg}>
                  <Path
                    d="M 20 30 Q 60 25, 100 20 T 180 15"
                    stroke="#22C55E"
                    strokeWidth={2}
                    fill="transparent"
                  />
                </Svg>
                {/* X-Axis Labels */}
                <View style={styles.chartXLabels}>
                  <Text style={styles.chartXLabel}>01-2025</Text>
                  <Text style={styles.chartXLabel}>02-2025</Text>
                  <Text style={styles.chartXLabel}>03-2025</Text>
                  <Text style={styles.chartXLabel}>04-2025</Text>
                </View>
                {/* Legend */}
                <View style={styles.chartLegend}>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: '#22C55E' }]} />
                    <Text style={styles.legendText}>{t('kpis.actualValue')}</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: '#3B82F6' }]} />
                    <Text style={styles.legendText}>{t('kpis.target')}</Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right: مؤشرات الأداء Section */}
          <View style={[styles.kpiSectionColumn, isSmallScreen && styles.kpiSectionColumnSmall]}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionTitleRow}>
                  <Text style={styles.sectionTitle}>{t('kpis.performanceIndicators')}</Text>
                  <View style={styles.percentageBadge}>
                    <SmallCircularProgress percentage={overallPercentage} size={32} />
                    <Text style={styles.percentageBadgeText}>%{overallPercentage}</Text>
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.addKpiButton}
                  onPress={() => navigation.navigate('AddEmployee', { employeeId: id })}
                >
                  <PlusIcon size={14} color="#414042" />
                  <Text style={styles.addKpiButtonText}>{t('kpis.addIndicator')}</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.kpiCardsBackground}>
                {kpisLoading ? (
                  <View style={styles.kpiLoadingContainer}>
                    <ActivityIndicator size="small" color={COLORS.primary} />
                    <Text style={styles.kpiLoadingText}>{t('kpis.loadingKPIs')}</Text>
                  </View>
                ) : kpis.length === 0 ? (
                  <View style={styles.noKpisContainer}>
                    <Text style={styles.noKpisText}>{t('employees.noKpisForEmployee')}</Text>
                  </View>
                ) : (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.kpiCardsScrollContent}
                    style={styles.kpiCardsScrollView}
                  >
                    {kpis.map((kpi) => (
                      <View key={kpi.id} style={styles.kpiCardWrapper}>
                        <KPICard
                          kpi={kpi}
                          onPress={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                          viewDetailsText={t('common.viewDetails')}
                        />
                      </View>
                    ))}
                  </ScrollView>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Row 3: العمليات الرئيسية Section (Full Width) - Shows processes where employee is assigned to steps */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleRow}>
              <Text style={styles.sectionTitle}>{t('processes.mainProcesses')}</Text>
              <View style={styles.percentageBadge}>
                <SmallCircularProgress percentage={parseFloat((processes.reduce((sum, p) => sum + (p.achievement_percentage || 0), 0) / (processes.length || 1)).toFixed(2))} size={32} />
                <Text style={styles.percentageBadgeText}>%{formatPct(processes.reduce((sum, p) => sum + (p.achievement_percentage || 0), 0) / (processes.length || 1))}</Text>
              </View>
            </View>
            <View style={{ width: 100 }} />
          </View>
          <View style={styles.kpiCardsBackground}>
            <View style={[styles.processCardsContainer, isSmallScreen && styles.processCardsContainerSmall]}>
              {processesLoading ? (
                <View style={styles.kpiLoadingContainer}>
                  <ActivityIndicator size="small" color={COLORS.primary} />
                  <Text style={styles.kpiLoadingText}>{t('employees.loadingProcesses')}</Text>
                </View>
              ) : processes.length === 0 ? (
                <View style={styles.noKpisContainer}>
                  <Text style={styles.noKpisText}>{t('employees.noProcessesAssigned')}</Text>
                </View>
              ) : (
                processes.map((process) => (
                  <ProcessCard
                    key={process.id}
                    name={process.name}
                    percentage={process.achievement_percentage || 0}
                    onViewDetails={() => navigation.navigate('ProcessDetail', { id: process.id })}
                    viewDetailsText={t('common.viewDetails')}
                  />
                ))
              )}
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('common.confirmDelete')}</Text>
            <Text style={styles.modalMessage}>
              {t('employees.confirmDeleteEmployee')} "{employee?.name_ar}"?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setDeleteModalVisible(false)}
              >
                <Text style={styles.modalCancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalDeleteButton, deleting && styles.modalButtonDisabled]}
                onPress={confirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.modalDeleteButtonText}>{t('common.delete')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  contentContainer: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 16,
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  retryButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbText: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: FONTS.regular,
  },
  breadcrumbSeparator: {
    fontSize: 12,
    color: '#9CA3AF',
    fontFamily: FONTS.regular,
  },
  breadcrumbActive: {
    fontSize: 12,
    color: '#22C55E',
    fontFamily: FONTS.medium,
  },
  // Header Section
  headerSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    paddingHorizontal: 16,
    marginBottom: 16,
    flexDirection: 'row-reverse', // RTL: employee info on right, edit button on left
    alignItems: 'center',
    justifyContent: 'space-between',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  editButton: {
    flexDirection: 'row-reverse', // RTL: icon on right, text on left
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5.5,
    paddingHorizontal: 12,
    minHeight: 32,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 1,
  },
  editButtonText: {
    fontSize: 14,
    color: '#414042',
    fontFamily: FONTS.regular,
  },
  employeeHeader: {
    flexDirection: 'row-reverse', // RTL: avatar on right, info on left
    alignItems: 'center',
    gap: 20,
  },
  avatarLarge: {
    width: 52,
    height: 52,
    borderRadius: 4,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 52,
    height: 52,
    borderRadius: 4,
  },
  employeeHeaderInfo: {
    alignItems: 'flex-end',
    gap: 8,
  },
  employeeNameLarge: {
    fontSize: 20,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.medium,
  },
  employeeJobTitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  // Main Content Layout
  mainContent: {
    flexDirection: 'row-reverse', // RTL: main content on right, side panel on left
    gap: 16,
  },
  mainContentSmall: {
    flexDirection: 'column-reverse',
  },
  rightColumn: {
    flex: 1,
  },
  rightColumnSmall: {
    flex: undefined,
  },
  leftColumn: {
    width: 280,
  },
  leftColumnSmall: {
    width: '100%',
    marginBottom: 16,
  },
  // KPI and Trend Chart Row
  kpiTrendRow: {
    flexDirection: 'row', // Trend chart on left, KPIs on right
    gap: 16,
    marginBottom: 16,
  },
  kpiTrendRowSmall: {
    flexDirection: 'column',
  },
  trendChartColumn: {
    width: 280,
  },
  trendChartColumnSmall: {
    width: '100%',
    marginBottom: 16,
  },
  kpiSectionColumn: {
    flex: 1,
  },
  kpiSectionColumnSmall: {
    flex: undefined,
  },
  // Cards Row
  cardsRow: {
    flexDirection: 'row-reverse', // RTL: cards start from right
    gap: 16,
    marginBottom: 16,
  },
  cardsRowSmall: {
    flexDirection: 'column',
  },
  card: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    padding: 12,
    gap: 16,
  },
  cardFullWidth: {
    flex: undefined,
    marginBottom: 12,
  },
  cardHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#FFFFFF',
  },
  cardTitle: {
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.medium,
  },
  cardTitleSimple: {
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  cardBody: {
    padding: 10,
    paddingHorizontal: 12,
  },
  cardBodySimple: {
    gap: 16,
  },
  statsCardLayout: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginTop: 12,
  },
  statsCircleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  statsCirclePercent: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: '#1F2937',
    marginTop: 4,
  },
  statsCircleLabel: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginTop: 2,
  },
  statsTableContainer: {
    flex: 1,
    gap: 12,
  },
  cardBodyStats: {
    padding: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Info Row
  infoRow: {
    flexDirection: 'row-reverse', // RTL: label box on right, value on left
    alignItems: 'center',
  },
  infoLabelBox: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoLabel: {
    fontSize: 11,
    color: '#64748B',
    textAlign: 'center',
    fontFamily: FONTS.regular,
  },
  infoValue: {
    flex: 1,
    fontSize: 11,
    color: '#1F2937',
    textAlign: 'right',
    paddingHorizontal: 8,
    fontFamily: FONTS.regular,
  },
  // Stats Progress Row
  statsProgressRow: {
    flexDirection: 'row-reverse', // RTL: label box on right, progress on left
    alignItems: 'center',
  },
  statsProgressContent: {
    flex: 1,
    flexDirection: 'row-reverse', // RTL: progress on left, text on right
    alignItems: 'center',
    gap: 8,
  },
  statsProgressValue: {
    fontSize: 14,
    color: '#1F2937',
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  // Stats
  statsGrid: {
    flex: 1,
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  statLabel: {
    fontSize: 11,
    color: '#6B7280',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  statValue: {
    fontSize: 11,
    color: '#1F2937',
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  circularProgressWrapper: {
    marginLeft: 16,
  },
  // Section
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row-reverse', // RTL: title on right, button on left
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitleRow: {
    flexDirection: 'row-reverse', // RTL: title before badge
    alignItems: 'center',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    color: '#414042',
    fontFamily: FONTS.medium,
  },
  percentageBadge: {
    backgroundColor: '#DCFCE7',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  percentageBadgeIcon: {
    width: 16,
    height: 16,
  },
  percentageBadgeText: {
    fontSize: 14,
    color: '#22C55E',
    fontFamily: FONTS.medium,
  },
  countBadge: {
    backgroundColor: '#F1F5F9',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  countBadgeText: {
    fontSize: 14,
    color: '#64748B',
    fontFamily: FONTS.medium,
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  addKpiButtonText: {
    fontSize: 14,
    color: '#414042',
    fontFamily: FONTS.regular,
  },
  // KPI Cards - Horizontal Scroll RTL
  kpiCardsScrollView: {
    // RTL support - flip the scroll view horizontally so it scrolls from right to left
    transform: [{ scaleX: -1 }],
  },
  kpiCardsScrollContent: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  kpiCardWrapper: {
    // Flip card back to normal orientation
    transform: [{ scaleX: -1 }],
  },
  kpiCardsBackground: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
  },
  kpiLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    gap: 10,
  },
  kpiLoadingText: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: FONTS.regular,
  },
  noKpisContainer: {
    alignItems: 'flex-end',
    padding: 20,
    width: '100%',
  },
  noKpisText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  // KPI Card - Unified with UnitGoalDetailScreen/ServiceDetailScreen
  kpiCard: {
    width: 280,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
  },
  kpiCardTitle: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  kpiCardStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  kpiValueBadge: {
    backgroundColor: '#F1F5F9',
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  kpiValueText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#414042',
    textAlign: 'center',
  },
  kpiCardProgress: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0,
  },
  kpiProgressBarBg: {
    flex: 1,
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    overflow: 'hidden',
    flexDirection: 'row-reverse',
  },
  kpiProgressBarFill: {
    height: 8,
    borderRadius: 8,
  },
  kpiProgressDotOuter: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    backgroundColor: '#FCFCFC',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -5,
    zIndex: 2,
  },
  kpiProgressDotInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#2CA02C',
  },
  kpiCardLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
    borderRadius: 4,
  },
  kpiCardLinkText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#414042',
    textAlign: 'right',
  },
  // Progress Bar - RTL (fills from right to left)
  progressBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    position: 'relative',
    flexDirection: 'row-reverse', // RTL: start from right
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressDot: {
    position: 'absolute',
    top: -3,
    width: 14,
    height: 14,
    borderRadius: 7,
    marginRight: -7, // RTL: adjusted for right-to-left
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  // Process Cards
  processCardsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  processCardsContainerSmall: {
    flexDirection: 'column',
  },
  processCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 16,
  },
  processCardTitle: {
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 12,
    fontFamily: FONTS.medium,
  },
  kpiProgressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  kpiPercentageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  kpiPercentage: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#22C55E',
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  kpiDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  processCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Side Cards
  sideCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  sideCardTitle: {
    fontSize: 16,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
    fontFamily: FONTS.medium,
  },
  // Chart
  chartWrapper: {
    paddingTop: 8,
  },
  chartGridContainer: {
    gap: 30,
  },
  chartGridRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  chartYLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    width: 35,
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  chartGridLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E2E8F0',
  },
  chartSvg: {
    position: 'absolute',
    top: 20,
    left: 40,
    right: 0,
  },
  chartXLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingLeft: 40,
  },
  chartXLabel: {
    fontSize: 9,
    color: '#9CA3AF',
    fontFamily: FONTS.regular,
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 11,
    color: '#64748B',
    fontFamily: FONTS.regular,
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    color: '#1F2937',
    textAlign: 'center',
    marginBottom: 12,
    fontFamily: FONTS.medium,
  },
  modalMessage: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
    fontFamily: FONTS.regular,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: FONTS.medium,
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#EF4444',
    alignItems: 'center',
  },
  modalButtonDisabled: {
    opacity: 0.7,
  },
  modalDeleteButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: FONTS.medium,
  },
});
